import * as React from 'react';
import styled from 'styled-components';

import Modal from '@joinforma/ui/components/Modal';
import Headline4 from '@joinforma/ui/components/Headline4';
import Headline6 from '@joinforma/ui/components/Headline6';
import Subtitle1 from '@joinforma/ui/components/Subheader1';
import Callout from '@joinforma/ui/components/Callout';
import Checkbox from '@joinforma/ui/components/Checkbox';
import SvgIcon from '@joinforma/ui/components/SvgIcon';
import { ReactComponent as FilledCheckSvg } from '@joinforma/ui/assets/icons/check-circle-filled.svg';
import {
  ButtonSize,
  ButtonVariant,
  SvgIconColor,
  SvgIconSize,
} from '@joinforma/ui/constants/properties';
import Button from '@joinforma/ui/components/Button';
import Body2 from '@joinforma/ui/components/Body2';

import hsaImg from 'assets/images/hsa-bulk-transfer.png';
import dayjs from 'utils/date-helper';
import { HSA_BULK_TRANSFER_ELEMENTS } from 'constants/testSelectors';
import {
  SUPPORT_HSA_FAQ_LINK,
  MemberHsaTransferType,
} from 'constants/hsaBulkTransfer';

const ModalTitle = styled(Headline4)``;

const Illustration = styled.img.attrs({
  src: hsaImg,
  alt: 'Consolidate your HSA onto Forma!',
})`
  width: 100%;
`;

const Subtitle = styled(Headline6)`
  margin-bottom: 16px;
`;

const ModalContent = styled(Body2)<{ marginBottom?: string }>`
  display: block;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ marginBottom }) =>
    marginBottom ? marginBottom : '16px'};
`;

const CheckIcon = styled(SvgIcon).attrs({
  icon: FilledCheckSvg,
  fill: SvgIconColor.INTERACTIVE,
  size: SvgIconSize.MEDIUM,
})`
  margin-right: 13.2px;
`;

const BenefitsContent = styled(Subtitle1)`
  font-weight: 500;
`;

const OptInCheckBox = styled(Checkbox)`
  margin-bottom: 27px;
`;

const PrimaryButton = styled(Button).attrs({
  size: ButtonSize.LARGE,
  variant: ButtonVariant.PRIMARY,
})`
  width: 100%;
  margin-bottom: 16px;
`;

const SecondaryButton = styled(Button).attrs({
  size: ButtonSize.LARGE,
  variant: ButtonVariant.SECONDARY,
})`
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CalloutContainer = styled.div`
  margin-bottom: 31px;
`;

const HSABenefit = styled.div<{ marginBottom?: string | null }>`
  display: flex;
  margin-bottom: ${({ marginBottom }) =>
    marginBottom ? marginBottom : '24px'};
`;

const Link = styled.a`
  color: ${({ theme }) => theme.colors.text.link};
`;

const CalloutContentText = styled(Body2)`
  line-height: 18px;
`;

const DateSpan = styled(Body2)`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 500;
`;

const CalloutContent = () => (
  <CalloutContentText>
    Learn more about your new Forma HSA on the{' '}
    <Link href={SUPPORT_HSA_FAQ_LINK} target="_blank" rel="noopener noreferrer">
      FAQ page.
    </Link>{' '}
    Reach out via live chat or{' '}
    <Link href="mailto:support@joinforma.com">support@joinforma.com</Link> for
    help.
  </CalloutContentText>
);

const benefits = [
  'Earn 0.15% APY on your HSA cash balance',
  'Invest your HSA money for tax-free growth',
  'Use your Forma HSA Debit Card to spend directly from your HSA',
];

interface NonAlegeusModalProps {
  isOpen: boolean;
  onClose: () => void;
  custodianName: string;
  transferDeadline: string;
  termsAndConditionsLink: string;
  handleOptIn: ({
    hsaAccountId,
    payload,
  }: {
    hsaAccountId: string;
    payload: {
      custodianName: string;
      transferType: MemberHsaTransferType;
      transferDeadline?: string;
    };
  }) => void;
  hsaAccountId: string;
}

const HSAConsolidateModal = ({
  isOpen,
  onClose,
  custodianName,
  transferDeadline,
  termsAndConditionsLink,
  handleOptIn,
  hsaAccountId,
}: NonAlegeusModalProps) => {
  const [isTermsConditionsChecked, setIsTermsConditionsChecked] =
    React.useState(false);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      customTitle={
        <ModalTitle>
          Consolidate your HSA balance to Forma and manage everything in one
          place, plus exclusive benefits!
        </ModalTitle>
      }
      customMaxWidth="680px"
      hideCloseButton
    >
      <Illustration />

      <Subtitle data-testid={HSA_BULK_TRANSFER_ELEMENTS.NON_ALEGEUS_MODAL}>
        Consolidate all your HSA money into one place
      </Subtitle>

      <ModalContent>
        Starting in 2023 you can manage your HSA cash and investments directly
        from Forma! All you have to do is opt-in before{' '}
        <DateSpan>{dayjs(transferDeadline).format('LL')}</DateSpan> and we'll
        take care of everything.
      </ModalContent>

      <ModalContent>
        Since you still have some money at {custodianName}, we can automatically
        consolidate it for you so it's all in one place. Plus extra benefits
        exclusive to Forma:
      </ModalContent>

      {benefits.map((b, idx, arr) => (
        <HSABenefit
          key={b}
          marginBottom={arr.length - 1 === idx ? '34px' : null}
        >
          <CheckIcon />
          <BenefitsContent>{b}</BenefitsContent>
        </HSABenefit>
      ))}

      <CalloutContainer>
        <Callout customContent={<CalloutContent />} />
      </CalloutContainer>
      <OptInCheckBox
        name="terms-conditions"
        checked={isTermsConditionsChecked}
        onChange={(v) => setIsTermsConditionsChecked(v)}
        data-testid={HSA_BULK_TRANSFER_ELEMENTS.NON_ALEGEUS_MODAL_TERMS}
      >
        I have read and agree to Forma's{' '}
        <Link
          href={termsAndConditionsLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          terms and agreements.
        </Link>
      </OptInCheckBox>

      <ButtonContainer>
        <PrimaryButton
          disabled={!isTermsConditionsChecked}
          onClick={() =>
            handleOptIn({
              hsaAccountId,
              payload: {
                custodianName,
                transferType: MemberHsaTransferType.Accept,
              },
            })
          }
          data-testid={HSA_BULK_TRANSFER_ELEMENTS.NON_ALEGEUS_MODAL_CTA}
        >
          Transfer my HSA money
        </PrimaryButton>

        <SecondaryButton onClick={() => onClose()}>
          Remind me later
        </SecondaryButton>
      </ButtonContainer>
    </Modal>
  );
};

export default HSAConsolidateModal;
